'use client'

import { MutableRefObject, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { usePortalTarget, PortalTargetId } from '../hooks/portal'

type PortalProps = {
  /**
   * Custom node element to render to. If not defined the children will be
   * rendered to the document body.
   **/
  node?: HTMLElement
  children?: React.ReactNode
  target?: PortalTargetId
  refCallback?: (arg0: MutableRefObject<HTMLElement | null>) => void
  className?: string
}

const createElement = (className?: string) => {
  const element = document.createElement('div')
  element.classList.add('portal') // Fix for storybook css
  if (className) {
    element.classList.add(...className.split(/\s+/))
  }

  return element as HTMLElement
}

/**
 * Creates a React Portal to the document body. Renders the components children
 * to the document body or a defined HTML node element.
 */
export const Portal = ({ children, node, target, refCallback, className }: PortalProps) => {
  const [isClient, setIsClient] = useState(false)
  const targetNode = usePortalTarget(target)
  const defaultNode = useRef<HTMLElement | null>(null)

  useEffect(() => {
    if (!defaultNode.current) {
      defaultNode.current = createElement(className)
    }
    if (refCallback) {
      refCallback(defaultNode)
    }
  }, [defaultNode, className])

  useEffect(() => {
    setIsClient(true)
  }, [])

  useEffect(() => {
    if (defaultNode.current && defaultNode.current.parentElement !== document.body) {
      document.body.appendChild(defaultNode.current)
    }

    if (node) {
      if (defaultNode.current) {
        if (defaultNode.current?.parentNode) {
          defaultNode.current.parentNode.removeChild(defaultNode.current)
        }
        defaultNode.current = null
      }
    } else if (!defaultNode.current) {
      defaultNode.current = createElement(className)
      document.body.appendChild(defaultNode.current)
    }

    //make sure node has enterd DOM tree
    setTimeout(() => refCallback && refCallback(defaultNode))

    return () => {
      if (defaultNode.current?.parentNode) {
        defaultNode.current.parentNode.removeChild(defaultNode.current)
      }
      defaultNode.current = null
    }
  }, [node, className])

  if (!isClient) {
    return null
  }

  if (node) {
    return createPortal(children, node)
  }
  if (targetNode) {
    return createPortal(children, targetNode)
  }
  if (defaultNode.current) {
    return createPortal(children, defaultNode.current)
  }
  return null
}
